import { useEffect, useState } from "react";
import { track } from "~/lib/segmentAnalytics";
import classnames from "classnames";
import styled from "styled-components";

import Button from "~/components/shared/Button";
import Dialog from "~/components/shared/Dialog";

import linuxLogo from "~/images/compute/machine-type-linux.png";
import macosLogo from "~/images/compute/machine-type-macos.png";
import windowsLogo from "~/images/compute/machine-type-windows.png";
import checkboxIndicator from "~/images/checkbox-indicator.svg";
import checkboxIndicatorChecked from "~/images/checkbox-indicator-checked.svg";

const FocusableLabel = styled.label`
  &:focus-within {
    border-color: var(--purple-600);
  }
`;

const MACHINE_TYPES = {
  linux: {
    value: "linux",
    label: "Linux",
    logo: linuxLogo,
  },
  macos: {
    value: "macos",
    label: "MacOS",
    logo: macosLogo,
  },
  windows: {
    value: "windows",
    label: "Windows",
    logo: windowsLogo,
  },
};

const BANNER_NAME = "compute expression of interest";

function ExpressionOfInterestForm({ organizationUuid, dismissNoticeUrl }) {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const [selectedStates, setSelectedStates] = useState({
    linux: false,
    macos: false,
    windows: false,
  });

  useEffect(() => {
    track("Promotional Banner Viewed", {
      banner_name: BANNER_NAME,
      organization_uuid: organizationUuid,
    });
  }, []);

  useEffect(() => {
    setSelectedStates({
      linux: selectAllChecked,
      macos: selectAllChecked,
      windows: selectAllChecked,
    });
  }, [selectAllChecked, setSelectedStates]);

  function handleSubmit(event) {
    event.preventDefault();

    const selectedMachineTypes = Object.keys(selectedStates).filter((key) => selectedStates[key]);

    track("Compute Expression of Interest Submitted", {
      organization_uuid: organizationUuid,
      machine_types: selectedMachineTypes.toString(),
    }).then(() => {
      setHasSubmitted(true);
    });
  }

  function handleMachineTypeSelectionChange(event) {
    const { value, checked } = event.target;
    setSelectedStates((prev) => ({ ...prev, [value]: checked }));
  }

  function handleToggleSelectAll() {
    setSelectAllChecked((prev) => !prev);
  }

  function handleCloseDialog() {
    setIsModalOpen(false);

    if (hasSubmitted && dismissNoticeUrl) {
      // The user's joined the waitlist so they don't need to see the notice anymore
      fetch(dismissNoticeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": window._csrf.token,
        },
        body: JSON.stringify({}),
      }).then(() => {
        window.location.reload();
      });
    }
  }

  function handleOpenDialog() {
    setIsModalOpen(true);
    track("Promotional Banner CTA Clicked", {
      banner_name: BANNER_NAME,
      organization_uuid: organizationUuid,
    });
  }

  return (
    <>
      <Button
        className="btn rounded-1.5 unstyled-button semi-bold purple-600 cursor-pointer border border-purple-300 bg-transparent px-2 py-1"
        onClick={handleOpenDialog}
      >
        Join waitlist
      </Button>
      <Dialog onRequestClose={handleCloseDialog} isOpen={isModalOpen} width={500}>
        <div className="p-5 text-center">
          {hasSubmitted ? (
            <h3 className="font-semibold">You&apos;re on the waitlist!</h3>
          ) : (
            <>
              <h3 className="font-semibold">Select your machine type</h3>
              <div className="gap3 flex flex-col items-center">
                <p className="normal m-0">
                  ⚡️ Unlock maximum velocity with the new hosted agents. Select your preferred
                  machine type and join our waitlist
                </p>
                <form onSubmit={handleSubmit} className="gap3 flex w-full flex-col items-center">
                  <div className="gap2 grid w-full grid-cols-3">
                    {Object.values(MACHINE_TYPES).map((machineType) => (
                      <MachineTypeInputCard
                        key={machineType.label}
                        value={machineType.value}
                        label={machineType.label}
                        checked={selectedStates[machineType.value]}
                        onChange={handleMachineTypeSelectionChange}
                        logo={machineType.logo}
                      />
                    ))}
                  </div>
                  <FocusableLabel
                    data-testid="select-all"
                    className="gap1 rounded-1.5 flex cursor-pointer border border-gray-400 px-5 py-2"
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      onChange={handleToggleSelectAll}
                      checked={selectAllChecked}
                    />
                    <img
                      src={selectAllChecked ? checkboxIndicatorChecked : checkboxIndicator}
                      width={20}
                      height={20}
                    />
                    <p className="semi-bold m-0">Select all</p>
                  </FocusableLabel>
                  <button
                    className="btn btn-onboarding"
                    style={{ width: "60%" }}
                    onClick={handleSubmit}
                    disabled={Object.values(selectedStates).every((state) => !state)}
                  >
                    Join waitlist
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}

function MachineTypeInputCard({ value, label, logo, checked, onChange }) {
  return (
    <FocusableLabel
      className={classnames(
        "gap2 p5 rounded-1.5 flex w-full cursor-pointer flex-col items-center border border-gray-400",
        { "border-purple-600 bg-purple-100": checked },
      )}
    >
      <input
        id={`checkbox-card-${value}`}
        type="checkbox"
        name="machineType"
        value={value}
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div>
        <img src={logo} width={40} height={40} alt={label} />
        <p id={`checkbox-card-${value}-label`} className="semi-bold m-0">
          {label}
        </p>
      </div>
      <img src={checked ? checkboxIndicatorChecked : checkboxIndicator} width={20} height={20} />
    </FocusableLabel>
  );
}

export default ExpressionOfInterestForm;
